import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useSEO, getOrganizationSchema, getLocalBusinessSchema, getWebsiteSchema } from '@/utils/seo';

const agencies = [
  {
    name: 'Med-Care Providers',
    tagline: 'Full-Spectrum Medical Care',
    description: 'Comprehensive outpatient center offering primary care, psychiatric evaluations, pain management, and specialized home medical visits.',
    services: ['Primary Care Physicians', 'Psychiatric Evaluations', 'Pain Management', 'Advanced Wound Care', 'Home Medical Visits'],
    badge: 'Medicare & Medicaid',
    icon: 'ri-stethoscope-line',
    accent: 'teal',
    patients: '3,200+',
    rating: 4.9,
  },
  {
    name: 'Celebrity Home Health',
    tagline: 'Skilled Nursing & Therapy',
    description: 'Medicare-certified agency delivering clinical nursing, physical therapy, occupational therapy, and IV therapy in the comfort of home.',
    services: ['Skilled Nursing Care', 'Physical Therapy', 'Occupational Therapy', 'Speech Therapy', 'IV Therapy'],
    badge: 'Medicare Certified',
    icon: 'ri-nurse-line',
    accent: 'emerald',
    patients: '2,100+',
    rating: 4.8,
  },
  {
    name: 'Accent Senior Care',
    tagline: 'Daily Living Support',
    description: 'Compassionate non-skilled care providing personal assistance, companionship, meal preparation, and reliable transportation for seniors.',
    services: ['Personal Care Assistance', 'Companion Services', 'Light Housekeeping', 'Meal Preparation', 'Transportation'],
    badge: 'Medicaid Approved',
    icon: 'ri-user-heart-line',
    accent: 'amber',
    patients: '1,500+',
    rating: 4.9,
  },
  {
    name: 'A Peace of Mind Caregivers',
    tagline: 'Memory & Dementia Care',
    description: 'Specialized dementia and Alzheimer\'s care with a compassion-first approach, cognitive activities, respite services, and VA support.',
    services: ["Alzheimer's Care", 'Memory Support Programs', 'Respite Services', 'Cognitive Activities', 'VA Trusted'],
    badge: 'Memory Specialists',
    icon: 'ri-brain-line',
    accent: 'rose',
    patients: '900+',
    rating: 5.0,
  },
];

const stats = [
  { number: '7,500+', label: 'Patients Served', icon: 'ri-user-heart-line' },
  { number: '4', label: 'Specialized Agencies', icon: 'ri-building-2-line' },
  { number: '24/7', label: 'Care Coordination', icon: 'ri-customer-service-line' },
  { number: '99%', label: 'Satisfaction Rate', icon: 'ri-medal-line' },
];

const quickActions = [
  { title: 'Emergency Care', desc: 'Urgent care request, 15-min response', icon: 'ri-alarm-warning-line', link: '/emergency', color: 'rose' },
  { title: 'Insurance Check', desc: 'Verify coverage instantly', icon: 'ri-shield-check-line', link: '/insurance', color: 'teal' },
  { title: 'Care Assessment', desc: 'Find your ideal provider match', icon: 'ri-heart-pulse-line', link: '/assessment', color: 'emerald' },
  { title: 'Talk to Expert', desc: 'Speak with a care coordinator', icon: 'ri-customer-service-line', link: '/consultation', color: 'amber' },
];

const testimonials = [
  {
    name: 'Jennifer Martinez',
    role: 'Family Caregiver',
    content: 'The Villarreal Health Network transformed how we found care for my father. Their team made the entire process seamless and completely stress-free.',
    rating: 5,
    location: 'Las Vegas, NV',
    initials: 'JM',
  },
  {
    name: 'Dr. Robert Kim',
    role: 'Hospital Discharge Planner',
    content: 'As a discharge planner, I depend on their network for reliable home health referrals. Always professional, responsive, and truly patient-focused.',
    rating: 5,
    location: 'Reno, NV',
    initials: 'RK',
  },
  {
    name: 'Patricia Williams',
    role: 'Patient',
    content: 'The psychiatric home health services changed my life. Having professional care in my own home made recovery so much more comfortable and dignified.',
    rating: 5,
    location: 'Henderson, NV',
    initials: 'PW',
  },
];

const accentClasses: Record<string, { bg: string; text: string; light: string; border: string }> = {
  teal: { bg: 'bg-teal-600', text: 'text-teal-700', light: 'bg-teal-50', border: 'border-teal-200' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200' },
  amber: { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50', border: 'border-amber-200' },
  rose: { bg: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-50', border: 'border-rose-200' },
};

function useCountUp(target: string, isVisible: boolean) {
  const [display, setDisplay] = useState('0');
  useEffect(() => {
    if (!isVisible) return;
    const numeric = parseFloat(target.replace(/[^0-9.]/g, ''));
    if (isNaN(numeric)) { setDisplay(target); return; }
    const suffix = target.replace(/[0-9.]/g, '');
    let start = 0;
    const duration = 1800;
    const step = 16;
    const increment = numeric / (duration / step);
    const timer = setInterval(() => {
      start = Math.min(start + increment, numeric);
      setDisplay((start < 10 ? Math.round(start * 10) / 10 : Math.round(start)).toString() + suffix);
      if (start >= numeric) clearInterval(timer);
    }, step);
    return () => clearInterval(timer);
  }, [isVisible, target]);
  return display;
}

function StatCard({ stat, isVisible }: { stat: typeof stats[0]; isVisible: boolean }) {
  const count = useCountUp(stat.number, isVisible);
  return (
    <div className="text-center">
      <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-3">
        <i className={`${stat.icon} text-teal-600 text-xl`}></i>
      </div>
      <div className="text-3xl font-bold text-stone-900 mb-1">{count}</div>
      <div className="text-sm text-stone-500 font-medium">{stat.label}</div>
    </div>
  );
}

export default function Home() {
  useSEO({
    title: 'The Villarreal Health Network - Home Healthcare Services in Las Vegas',
    description: 'The Villarreal Health Network connects you with trusted home healthcare providers in Las Vegas. Expert care coordination for skilled nursing, personal care, therapy services, and specialized medical support.',
    keywords: 'home healthcare Las Vegas, skilled nursing, personal care, therapy services, medical care coordination',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [getOrganizationSchema(), getLocalBusinessSchema(), getWebsiteSchema()],
    },
  });

  const [heroLoaded, setHeroLoaded] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/cb4200a2c8f934aaf4c0d8b5bb3dd562.jpg"
          alt="Professional home healthcare"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/80 via-stone-900/50 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-16">
          <div className={`max-w-2xl transition-all duration-1000 delay-200 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 text-teal-200 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse"></span>
              Trusted Healthcare Network · Las Vegas, NV
            </span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Compassionate<br />
              <span className="text-teal-300">Home Care,</span><br />
              Expertly Matched
            </h1>
            <p className="text-lg text-stone-200 mb-8 leading-relaxed max-w-xl">
              We connect Nevada families with exceptional home healthcare providers — from skilled nursing to memory care — with dedicated coordination every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/referral"
                className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3.5 rounded-xl transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-right-circle-line text-lg"></i>
                Start Your Care Journey
              </Link>
              <Link
                to="/compare"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-6 py-3.5 rounded-xl backdrop-blur-sm transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-building-2-line text-lg"></i>
                View Our Network
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 animate-bounce">
          <span className="text-white/50 text-xs tracking-widest uppercase">Scroll</span>
          <i className="ri-arrow-down-line text-white/50"></i>
        </div>
      </section>

      {/* ── QUICK ACTIONS ── */}
      <section className="bg-stone-50 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action) => {
              const ac = accentClasses[action.color];
              return (
                <Link
                  key={action.title}
                  to={action.link}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-stone-100 hover:border-stone-200 hover:-translate-y-0.5 transition-all duration-200 group"
                >
                  <div className={`w-9 h-9 ${ac.light} ${ac.text} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <i className={`${action.icon} text-base`}></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-stone-800 group-hover:text-teal-700 transition-colors duration-150 whitespace-nowrap">{action.title}</div>
                    <div className="text-xs text-stone-400 mt-0.5 leading-snug">{action.desc}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsRef} className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {stats.map((stat, i) => (
              <StatCard key={i} stat={stat} isVisible={statsVisible} />
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT STRIP ── */}
      <section className="bg-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
                Nevada's Most Trusted Care Network
              </h2>
              <p className="text-teal-100 text-base leading-relaxed">
                Since 2020, The Villarreal Health Network has united four specialized agencies under one coordinated platform — making quality home healthcare accessible, personal, and seamless for every family we serve.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link
                to="/overview"
                className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 font-semibold px-5 py-3 rounded-xl hover:bg-teal-50 transition-colors duration-200 whitespace-nowrap text-sm"
              >
                <i className="ri-information-line"></i>
                Our Story
              </Link>
              <a
                href="tel:+17025551234"
                className="inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-5 py-3 rounded-xl transition-colors duration-200 whitespace-nowrap text-sm border border-teal-400"
              >
                <i className="ri-phone-line"></i>
                (702) 555-1234
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUR NETWORK ── */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="text-teal-600 font-semibold text-sm uppercase tracking-wider mb-2">Healthcare Network</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Four Agencies, One Network
            </h2>
            <p className="text-stone-500 text-base max-w-xl">
              Each agency in our network specializes in a different dimension of care — together, they cover virtually every home healthcare need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {agencies.map((agency, idx) => {
              const ac = accentClasses[agency.accent];
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-stone-100 p-6 hover:border-stone-200 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 ${ac.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <i className={`${agency.icon} text-white text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-stone-900 text-base">{agency.name}</h3>
                        <p className={`text-xs font-semibold ${ac.text} mt-0.5`}>{agency.tagline}</p>
                      </div>
                    </div>
                    <span className={`${ac.light} ${ac.text} text-xs font-semibold px-2.5 py-1 rounded-full border ${ac.border} whitespace-nowrap`}>
                      {agency.badge}
                    </span>
                  </div>

                  <p className="text-stone-500 text-sm leading-relaxed mb-4">{agency.description}</p>

                  <ul className="space-y-1.5 mb-5 flex-1">
                    {agency.services.map((s) => (
                      <li key={s} className="flex items-center gap-2 text-sm text-stone-600">
                        <i className={`ri-check-line ${ac.text} text-sm flex-shrink-0`}></i>
                        {s}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill text-xs ${i < Math.floor(agency.rating) ? 'text-amber-400' : 'text-stone-200'}`}></i>
                        ))}
                      </div>
                      <span className="text-xs text-stone-400 font-medium">{agency.rating} · {agency.patients} patients</span>
                    </div>
                    <Link
                      to="/referral"
                      className={`flex items-center gap-1 ${ac.text} text-sm font-semibold hover:underline whitespace-nowrap`}
                    >
                      Get Started <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/compare"
              className="inline-flex items-center gap-2 border border-stone-200 text-stone-700 hover:border-teal-500 hover:text-teal-700 font-semibold px-5 py-3 rounded-xl transition-all duration-200 text-sm"
            >
              <i className="ri-layout-column-line"></i>
              Compare All Agencies
            </Link>
          </div>
        </div>
      </section>

      {/* ── CARE TYPES ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-sm uppercase tracking-wider mb-2">What We Offer</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900">Comprehensive Care Solutions</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-nurse-line',
                title: 'Skilled Medical Care',
                desc: 'Advanced nursing, wound care, medication management, and specialized clinical services delivered at home.',
                color: 'teal',
                img: 'https://readdy.ai/api/search-image?query=professional%20nurse%20in%20scrubs%20checking%20patient%20vitals%20at%20home%2C%20modern%20medical%20equipment%2C%20bright%20clean%20room%2C%20natural%20daylight%2C%20warm%20professional%20atmosphere%2C%20real%20photo&width=600&height=400&seq=care-medical-v1&orientation=landscape',
              },
              {
                icon: 'ri-heart-line',
                title: 'Personal Care',
                desc: 'Compassionate support with daily activities, personal hygiene, mobility, and maintaining independence.',
                color: 'emerald',
                img: 'https://readdy.ai/api/search-image?query=kind%20caregiver%20assisting%20elderly%20person%20with%20daily%20activities%20at%20home%2C%20warm%20cozy%20interior%2C%20soft%20natural%20light%2C%20genuine%20care%20and%20warmth%2C%20clean%20modern%20home%20environment%2C%20real%20photography&width=600&height=400&seq=care-personal-v1&orientation=landscape',
              },
              {
                icon: 'ri-brain-line',
                title: 'Memory & Dementia',
                desc: "Specialized Alzheimer's and dementia care with cognitive activities, structured routines, and family support.",
                color: 'rose',
                img: 'https://readdy.ai/api/search-image?query=compassionate%20caregiver%20doing%20puzzle%20activities%20with%20elderly%20dementia%20patient%20at%20kitchen%20table%2C%20warm%20home%20setting%2C%20soft%20golden%20lighting%2C%20genuine%20smiles%2C%20calm%20and%20safe%20atmosphere%2C%20real%20photography&width=600&height=400&seq=care-memory-v1&orientation=landscape',
              },
            ].map((card) => {
              const ac = accentClasses[card.color];
              return (
                <div key={card.title} className="rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all duration-300 hover:-translate-y-1">
                  <div className="h-48 overflow-hidden">
                    <img src={card.img} alt={card.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6">
                    <div className={`w-9 h-9 ${ac.light} ${ac.text} rounded-lg flex items-center justify-center mb-3`}>
                      <i className={`${card.icon} text-base`}></i>
                    </div>
                    <h3 className="font-bold text-stone-900 text-base mb-2">{card.title}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-semibold text-sm uppercase tracking-wider mb-2">The Process</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Contact Us', desc: 'Reach out by phone, form, or chat — 24/7 availability for urgent needs.', icon: 'ri-phone-line' },
              { step: '02', title: 'Assessment', desc: 'Our intake specialists evaluate your needs, insurance, and preferences.', icon: 'ri-clipboard-line' },
              { step: '03', title: 'Matched', desc: 'We match you with the right agency from our specialized network.', icon: 'ri-team-line' },
              { step: '04', title: 'Care Begins', desc: 'Your care team is in place and services start on your schedule.', icon: 'ri-heart-pulse-line' },
            ].map((item, i) => (
              <div key={i} className="relative bg-white rounded-2xl border border-stone-100 p-6">
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-xs font-bold text-teal-500 bg-teal-50 border border-teal-100 px-2 py-1 rounded-md">{item.step}</span>
                </div>
                <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-teal-600 text-lg`}></i>
                </div>
                <h3 className="font-bold text-stone-800 mb-2">{item.title}</h3>
                <p className="text-stone-500 text-sm leading-relaxed">{item.desc}</p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 -translate-y-1/2 w-6 z-10">
                    <i className="ri-arrow-right-line text-stone-300 text-lg"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <p className="text-teal-600 font-semibold text-sm uppercase tracking-wider mb-2">Testimonials</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900">What Families Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-stone-50 rounded-2xl p-6 border border-stone-100 flex flex-col">
                <div className="flex items-center gap-0.5 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <p className="text-stone-600 text-sm leading-relaxed mb-5 flex-1 italic">&ldquo;{t.content}&rdquo;</p>
                <div className="flex items-center gap-3 pt-4 border-t border-stone-200">
                  <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-700 text-xs font-bold">{t.initials}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">{t.name}</p>
                    <p className="text-stone-400 text-xs">{t.role} · {t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 bg-stone-900 text-white relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20healthcare%20team%20meeting%20in%20a%20modern%20clinic%20hallway%2C%20warm%20ambient%20lighting%2C%20clean%20neutral%20colors%2C%20blurred%20background%2C%20real%20photographic%20style%2C%20warm%20beige%20tones&width=1920&height=600&seq=cta-bg-v2&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center opacity-20"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Ready to Find the Right Care?
          </h2>
          <p className="text-stone-300 text-base mb-8 max-w-lg mx-auto leading-relaxed">
            Our intake coordinators are available around the clock to guide you to the perfect provider for your loved one's needs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/referral"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3.5 rounded-xl transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-file-text-line"></i>
              Submit a Referral
            </Link>
            <a
              href="tel:+17025551234"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-6 py-3.5 rounded-xl backdrop-blur-sm transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              (702) 555-1234
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
